import { useQueryClient } from '@tanstack/react-query';
import { webRoutes } from '@runhuman/shared';
import { WEB_BASE_URL } from '../web-base-url';
import { ACTIVITY_SESSION_QUERY_KEY } from '../use-activity-session';
import { openInBrowser } from './tester/harness';
import { ExternalLinkIcon } from '../ExternalLinkIcon';

/** Deep link to the account settings page where Discord linking is managed. */
const ACCOUNT_SETTINGS_URL = new URL(webRoutes.settingsAccount.build(), WEB_BASE_URL).toString();

interface Props {
  readonly onCheckAgain: () => void;
  readonly isChecking: boolean;
}

/**
 * Rendered when the authenticated Discord user has no `users.discord_user_id`
 * row in Runhuman — i.e. they have a Discord account but never linked it to
 * a Runhuman account from the web app's account settings.
 *
 * This bypasses the auth-guard 403 the user would otherwise hit on the next
 * tester/admin endpoint and gives them a one-click handoff to the web flow
 * via the Embedded SDK's external-link command (the iframe can't navigate
 * to arbitrary URLs directly, but the SDK can pop a browser tab).
 *
 * Recovery for #4032: the Discord SDK's `openExternalLink` is fire-and-forget
 * (no resolve-on-tab-close signal), and Discord's iframe doesn't reliably
 * fire focus/visibility events across all clients (desktop/mobile/web).
 * Two-pronged recovery so the user is never trapped on this screen after
 * they finish linking:
 *   1. Invalidate the cached session query on the link click — the next
 *      focus event (when it does fire) refetches fresh data automatically.
 *   2. An explicit "I've linked — check again" button that triggers an
 *      immediate refetch on demand — guarantees an escape hatch on every
 *      client, regardless of whether focus events fire.
 */
export function UnlinkedView({ onCheckAgain, isChecking }: Props) {
  const queryClient = useQueryClient();

  const handleConnect = () => {
    void openInBrowser(ACCOUNT_SETTINGS_URL);
    void queryClient.invalidateQueries({ queryKey: [ACTIVITY_SESSION_QUERY_KEY] });
  };

  return (
    <div className="view view--unlinked">
      <h1>Verify your Discord identity</h1>
      <p>
        We need to match your Discord account to a Runhuman account before we
        can show you internal tester and ops content. This is just an identity
        check — we don't pull anything else from your Discord profile.
      </p>
      <p>
        Sign in on the web app and link Discord from your account settings.
        Once linked, this Activity will load your tester queue or ops dashboard
        automatically.
      </p>
      <button
        type="button"
        className="view--unlinked__cta"
        onClick={handleConnect}
      >
        Open account settings
        <ExternalLinkIcon />
      </button>
      <p className="view--unlinked__hint">
        Don't have a Runhuman account? You can sign up from the same page.
      </p>
      <div className="view--unlinked__check-again">
        <p className="view--unlinked__hint">
          Already linked? Click below to refresh.
        </p>
        <button
          type="button"
          className="view--unlinked__recheck"
          onClick={onCheckAgain}
          disabled={isChecking}
        >
          {isChecking ? 'Checking…' : "I've linked — check again"}
        </button>
      </div>
    </div>
  );
}
